import { useCallback, useEffect, useRef, useState } from 'react';
import { GetSupervisorAction } from '../../../libs/GraphQL';

// Reads the current user's supervisor actions for a single reviewDate and
// exposes the newest action per (reviewPhase, eventType) so the Line Haul /
// Headcount forms can hydrate. Current-user scoped per the BE contract; the
// query filters by reviewDate only. Module-level cache survives tab switches;
// a page reload clears it.
const cache = new Map();
const inFlight = new Map();

function keyOf(action) {
  return `${action.reviewPhase}__${action.eventType}`;
}

// Build a { `${phase}__${eventType}`: dataObject } map keeping the FIRST seen
// row per key. The BE returns newest-first, so first-seen == newest.
function buildActionsByKey(actions) {
  const map = {};
  (actions || []).forEach((action) => {
    if (!action || !action.reviewPhase || !action.eventType) return;
    const k = keyOf(action);
    if (!(k in map)) map[k] = action.data || {};
  });
  return map;
}

async function fetchForDate(reviewDate) {
  if (cache.has(reviewDate)) return cache.get(reviewDate);
  if (inFlight.has(reviewDate)) return inFlight.get(reviewDate);
  const promise = GetSupervisorAction({ reviewDate })
    .then((actions) => {
      const built = buildActionsByKey(actions);
      cache.set(reviewDate, built);
      inFlight.delete(reviewDate);
      return built;
    })
    .catch((err) => {
      inFlight.delete(reviewDate);
      throw err;
    });
  inFlight.set(reviewDate, promise);
  return promise;
}

export default function useSupervisorActions({ reviewDate } = {}) {
  const [actionsByKey, setActionsByKey] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!reviewDate) {
      setActionsByKey({});
      setIsLoading(false);
      return;
    }
    if (cache.has(reviewDate)) {
      setActionsByKey(cache.get(reviewDate));
    }
    setIsLoading(true);
    fetchForDate(reviewDate)
      .then((built) => {
        if (!mountedRef.current) return;
        setActionsByKey(built);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.error('[useSupervisorActions] fetch failed:', err);
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // `refreshTick` (bumped by refresh()) forces a re-fetch; refresh() drops
    // the cache for `reviewDate` first so the fetch hits the network.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewDate, refreshTick]);

  const refresh = useCallback(() => {
    if (reviewDate) cache.delete(reviewDate);
    setRefreshTick((t) => t + 1);
  }, [reviewDate]);

  return { actionsByKey, isLoading, refresh };
}

// Test-only helper: clear the module-level cache between specs.
export function resetSupervisorActionsCacheForTests() {
  cache.clear();
  inFlight.clear();
}
